import { useEffect, useRef, useState } from "react";
import { URLS } from "./URLS";

/* ============================================================
   Activity tracker
   ------------------------------------------------------------
   Counts time while the app is open and being used.

   A second is ACTIVE when the tab is visible and there has been
   keyboard, mouse or touch input within IDLE_AFTER. Everything
   else — a locked screen, a switched tab, a walk away from the
   desk — is IDLE, which is what the UI calls break time.

   Locking the screen fires visibilitychange in every current
   browser, so it is caught immediately rather than waiting out
   the idle timer.

   What it cannot see: time with the app closed entirely. The
   figures are "time at the app", and the UI says so.
   ============================================================ */

const IDLE_AFTER_MS = 5 * 60 * 1000;   /* 5 minutes, as asked */
const TICK_MS = 5000;                  /* how often a slice is counted */
const FLUSH_MS = 60000;                /* how often it is sent up */

const ACTIVITY_EVENTS = [
    "mousemove", "mousedown", "keydown",
    "wheel", "touchstart", "scroll",
];

export const useAttendanceTracker = ({ enabled, fetchData }) => {

    /* what has not been sent yet */
    const pending = useRef({ active: 0, idle: 0 });
    const lastInput = useRef(Date.now());
    const lastTick = useRef(Date.now());

    const [session, setSession] = useState({ activeSeconds: 0, idleSeconds: 0, idle: false });

    useEffect(() => {
        if (!enabled) return undefined;

        const noteInput = () => { lastInput.current = Date.now(); };
        ACTIVITY_EVENTS.forEach((e) =>
            window.addEventListener(e, noteInput, { passive: true }));

        const onVisibility = () => {
            /* coming back does not count the time away as active */
            if (!document.hidden) lastInput.current = Date.now();
            lastTick.current = Date.now();
        };
        document.addEventListener("visibilitychange", onVisibility);

        const tick = setInterval(() => {
            const now = Date.now();

            /* Measure the real gap rather than assuming TICK_MS — a throttled
               background tab fires this far less often than it asks to. */
            const elapsed = Math.round((now - lastTick.current) / 1000);
            lastTick.current = now;
            if (elapsed <= 0) return;

            const away = document.hidden || (now - lastInput.current) > IDLE_AFTER_MS;

            if (away) pending.current.idle += elapsed;
            else pending.current.active += elapsed;

            setSession((prev) => ({
                activeSeconds: prev.activeSeconds + (away ? 0 : elapsed),
                idleSeconds: prev.idleSeconds + (away ? elapsed : 0),
                idle: away,
            }));
        }, TICK_MS);

        const flush = async () => {
            const { active, idle } = pending.current;
            if (!active && !idle) return;

            pending.current = { active: 0, idle: 0 };

            const resp = await fetchData({
                url: URLS.attendanceHeartbeat,
                method: "post",
                body: { activeSeconds: active, idleSeconds: idle },
            });

            /* put it back if the server did not take it, so nothing is lost
               to a dropped connection */
            if (resp?.success === false) {
                pending.current.active += active;
                pending.current.idle += idle;
            }
        };

        const flusher = setInterval(flush, FLUSH_MS);

        /* a closing tab gets one last chance to report */
        const onLeave = () => {
            const { active, idle } = pending.current;
            if (!active && !idle) return;
            try {
                navigator.sendBeacon?.(
                    `/api/v1/${URLS.attendanceHeartbeat}`,
                    new Blob([JSON.stringify({ activeSeconds: active, idleSeconds: idle })],
                        { type: "application/json" }));
            } catch (e) { /* nothing useful to do while unloading */ }
        };
        window.addEventListener("pagehide", onLeave);

        return () => {
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, noteInput));
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", onLeave);
            clearInterval(tick);
            clearInterval(flusher);
            flush();
        };
    }, [enabled, fetchData]);

    return session;
};

export default useAttendanceTracker;
